import { useCallback, useEffect, useMemo, useState } from "react";
import { callFunction } from "../api/callFunction";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import {
  XMarkIcon,
  CheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { db } from "../firebase";
import EventAPI from "../api/EventApi";
import { showToast } from "../utils/showToast";
import { toDateSafe, formatEventDate } from "./formSectionHelpers";
import { countEventGuests } from "./utils/eventGuests";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

const STEPS = [
  { key: "refund", label: "Refund attendees" },
  { key: "note", label: "Write a note" },
  { key: "discount", label: "Offer a discount" },
  { key: "review", label: "Review & finalize" },
];

const INPUT =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const LABEL =
  "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1";

// Seed a coupon code from a memorable word in the event title (e.g. "Trivia
// Night" -> "TRIVIA"). Skips short words and filler; falls back to `fallback`
// when the title has nothing usable.
const CODE_STOPWORDS = new Set([
  "the", "and", "for", "with", "your", "our", "from", "you", "this", "that",
  "are", "was",
]);
const codeFromTitle = (title, fallback) => {
  const words = String(title || "")
    .split(/[^a-zA-Z0-9]+/)
    .filter((w) => w.length >= 3 && w.length <= 15 && !CODE_STOPWORDS.has(w.toLowerCase()));
  if (words.length === 0) return fallback;
  return words[Math.floor(Math.random() * words.length)].toUpperCase();
};

// Refund ledger reading. A payment is "handled" (won't block the cancel) once its
// refund is settled OR in flight. This mirrors the cancelEvent guard exactly
// (amountRefundedCents>0 || status processing|completed|refunded) so the wizard's
// "all refunded" state can't drift out of sync with what the backend enforces.
const refundStateOf = (ledger) => {
  if (!ledger) return null; // still refundable
  if ((ledger.amountRefundedCents || 0) > 0 || ledger.status === "refunded" || ledger.status === "completed")
    return "refunded";
  if (ledger.status === "processing") return "processing";
  return null;
};

const Spinner = ({ className = "" }) => (
  <div className={`animate-spin rounded-full border-2 border-gray-200 dark:border-gray-700 border-t-primary ${className}`} />
);

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <div key={s.key} className="flex items-center gap-2 min-w-0">
            <div
              className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                done
                  ? "bg-primary text-white"
                  : active
                  ? "bg-primary/15 text-primary ring-1 ring-primary"
                  : "bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500"
              }`}
            >
              {done ? <CheckIcon className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span
              className={`hidden sm:block truncate text-xs ${
                active
                  ? "font-medium text-gray-900 dark:text-gray-100"
                  : "text-gray-400 dark:text-gray-500"
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="mx-1 hidden h-px w-4 flex-shrink-0 bg-gray-200 dark:bg-gray-700 sm:block" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CancelEventWizard({ eventData, allowAnyEvent = false, onClose, onCancelled }) {
  const eventID = eventData?.id;
  const eventName = eventData?.name || "this event";
  // A true super admin (signed superAdmin claim) can discount ANY upcoming event
  // and select several at once (multi-event coupon). Everyone else — hosts and
  // flag-only operators the coupon backend won't accept for other hosts' events —
  // is scoped to THIS host's own events, single pick.
  const hostVendorID = eventData?.vendorID;

  const [step, setStep] = useState(0);

  // ---- Step 1: refunds (previewed here, actually issued at finish) ----
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState({}); // paymentIntentId -> ledger doc
  const [rowError, setRowError] = useState({}); // paymentIntentId -> message (set if a refund fails at finish)

  // ---- Step 2: note ----
  const [note, setNote] = useState("");
  const [internalReason, setInternalReason] = useState("");

  // ---- Step 3: discount ----
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [otherEvents, setOtherEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [targetEventIDs, setTargetEventIDs] = useState([]);
  const [eventSearch, setEventSearch] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [percentageOff, setPercentageOff] = useState(20);
  const [code, setCode] = useState("");
  const [maxUses, setMaxUses] = useState(50);
  // The host-facing discount line. Pre-filled from a template and kept in sync
  // with the discount config until the host edits it (then their text sticks).
  const [discountMessage, setDiscountMessage] = useState("");
  const [messageEdited, setMessageEdited] = useState(false);
  const [couponCreated, setCouponCreated] = useState(false);

  // ---- Step 4: finalize ----
  const [finalizing, setFinalizing] = useState(false);
  const [progress, setProgress] = useState("");

  const attendeeCount = useMemo(() => countEventGuests(eventData), [eventData]);

  // Load the event's payments + existing refunds. On failure we set loadError
  // rather than falling open — otherwise an empty `payments` would masquerade as a
  // free event and let the host skip the refund gate on a paid one.
  const loadPayments = useCallback(async () => {
    if (!eventID) return null;
    setLoadingPayments(true);
    setLoadError(false);
    try {
      const [pmts, rfnds] = await Promise.all([
        EventAPI.getProcessedPayments(eventID),
        EventAPI.getRefundedPayments(eventID),
      ]);
      setPayments(pmts || []);
      setRefunds(rfnds || {});
      return { payments: pmts || [], refunds: rfnds || {} };
    } catch (err) {
      console.error("Failed to load event payments:", err);
      setLoadError(true);
      return null;
    } finally {
      setLoadingPayments(false);
    }
  }, [eventID]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  // Seed a sensible default message once we know whether the event had payments.
  useEffect(() => {
    if (loadingPayments || note) return;
    const d = toDateSafe(eventData?.date);
    const on = d ? ` on ${formatEventDate(d)}` : "";
    // The note goes to EVERYONE who RSVP'd, and not all of them paid — so the
    // refund line is conditional ("if you bought a ticket") rather than telling a
    // free attendee they were refunded.
    const hadPaid = (payments || []).length > 0;
    setNote(
      `We're really sorry, but "${eventName}"${on} has been cancelled.` +
        (hadPaid
          ? " If you bought a ticket, you've been fully refunded. It can take a few days to show on your statement."
          : "")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPayments]);

  // Any change to the discount config invalidates a coupon we may have created on
  // a prior finalize attempt, so a retry re-creates it with the fresh code.
  const targetKey = targetEventIDs.join(",");
  useEffect(() => {
    setCouponCreated(false);
  }, [discountEnabled, discountType, percentageOff, code, maxUses, targetKey]);

  // Load the discount picker's events. A true super admin (allowAnyEvent) gets
  // EVERY upcoming event; everyone else is scoped to this host's own events (the
  // coupon backend only lets them manage those). Same query shapes the Coupons
  // page uses, so no new index.
  useEffect(() => {
    if (!discountEnabled || otherEvents.length || loadingEvents) return;
    if (!allowAnyEvent && !hostVendorID) return;
    const load = async () => {
      setLoadingEvents(true);
      try {
        const constraints = [where("isCompleted", "==", false)];
        if (!allowAnyEvent) constraints.push(where("vendorID", "==", hostVendorID));
        constraints.push(orderBy("date", "asc"), limit(200));
        const snap = await getDocs(query(collection(db, "events"), ...constraints));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => e.id !== eventID && e.status !== "cancelled");
        setOtherEvents(list);
      } catch (err) {
        console.error("Failed to load events for discount:", err);
      } finally {
        setLoadingEvents(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountEnabled]);

  const paymentState = (pi) => refundStateOf(refunds[pi]);
  // Tickets not yet refunded — these are what "finish" will refund.
  const pendingRefunds = payments.filter((p) => paymentState(p.paymentIntentId) === null);
  const pendingRefundTotal = pendingRefunds.reduce((s, p) => s + (Number(p.amountDollars) || 0), 0);

  // Issue refunds for every not-yet-refunded ticket. Runs at finish (not up front)
  // so the whole cancellation executes as one action. Returns the count that
  // failed so finalize can stop before cancelling (the backend blocks a cancel
  // while any paid ticket is unrefunded). Idempotent: refundEventTicket dedupes,
  // and an "already-exists" means the backend already has the refund → treat as done.
  const runRefunds = async () => {
    const targets = payments.filter((p) => paymentState(p.paymentIntentId) === null);
    if (targets.length === 0) return 0;
    let failed = 0;
    await Promise.all(
      targets.map(async (p) => {
        const pi = p.paymentIntentId;
        setRowError((prev) => {
          const next = { ...prev };
          delete next[pi];
          return next;
        });
        try {
          await EventAPI.refundEventTicket(eventID, pi);
          setRefunds((prev) => ({ ...prev, [pi]: { ...(prev[pi] || {}), status: "processing" } }));
        } catch (err) {
          const alreadyRefunded =
            String(err?.code || "").includes("already-exists") ||
            /already been refunded/i.test(err?.message || "");
          if (alreadyRefunded) {
            setRefunds((prev) => ({ ...prev, [pi]: { ...(prev[pi] || {}), status: "processing" } }));
          } else {
            failed += 1;
            setRowError((prev) => ({ ...prev, [pi]: err?.message || "Refund failed." }));
          }
        }
      })
    );
    return failed;
  };

  const toggleTarget = (id) =>
    setTargetEventIDs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const enableDiscount = (on) => {
    setDiscountEnabled(on);
    if (on && !code.trim()) {
      const generic = discountType === "free" ? "ONUS" : `SAVE${Number(percentageOff) || ""}`;
      setCode(codeFromTitle(eventData?.name, generic));
    }
    if (on && !maxUses) setMaxUses(Math.max(attendeeCount, 10));
  };

  // The suggested wording, derived from the discount config.
  const defaultDiscountMessage = useMemo(() => {
    const c = code.trim().toUpperCase() || "CODE";
    if (discountType === "free")
      return `As a small sorry, your next event is on us. Use code ${c} at checkout.`;
    return `As a small sorry, here's ${Number(percentageOff) || 0}% off your next event. Use code ${c} at checkout.`;
  }, [discountType, percentageOff, code]);

  // Keep the editable message in sync with the config until the host takes it over,
  // so a percentage/code change doesn't leave stale copy — but a host edit sticks.
  useEffect(() => {
    if (discountEnabled && !messageEdited) setDiscountMessage(defaultDiscountMessage);
  }, [defaultDiscountMessage, discountEnabled, messageEdited]);

  const onDiscountMessageChange = (value) => {
    setDiscountMessage(value);
    setMessageEdited(true);
  };
  const resetDiscountMessage = () => {
    setDiscountMessage(defaultDiscountMessage);
    setMessageEdited(false);
  };

  // The code must appear in the message, since that's the only place attendees see it.
  const codeInMessage =
    !code.trim() ||
    discountMessage.toUpperCase().includes(code.trim().toUpperCase());

  const discountLine =
    discountEnabled && targetEventIDs.length > 0 && code.trim() && discountMessage.trim()
      ? discountMessage.trim()
      : "";

  // Compose within the 600-char cap the backend also enforces, but trim the base
  // note (never the discount line) so a long message can't chop the coupon code.
  const finalNote = (() => {
    const base = note.trim();
    if (!discountLine) return base.slice(0, 600);
    const suffix = `\n\n${discountLine}`;
    return base.slice(0, Math.max(0, 600 - suffix.length)) + suffix;
  })();

  const discountValid =
    !discountEnabled ||
    (targetEventIDs.length > 0 &&
      code.trim().length > 0 &&
      (discountType !== "percentage" ||
        (Number(percentageOff) >= 1 && Number(percentageOff) <= 100)) &&
      Number(maxUses) >= 1 &&
      discountMessage.trim().length > 0 &&
      codeInMessage);

  const canProceed = (() => {
    // Nothing executes until finish, so each step only gates on valid input.
    if (step === 0) return !loadingPayments && !loadError;
    if (step === 1) return note.trim().length > 0;
    if (step === 2) return discountValid;
    return true;
  })();

  // Finish: run everything the host lined up, in one shot, in the only safe order —
  // refunds first (the backend blocks a cancel while any paid ticket is unrefunded),
  // then the discount coupon (best-effort), then the cancel + notification.
  const finalize = async () => {
    setFinalizing(true);
    try {
      // 1. Refunds. If any fail, stop before cancelling and send the host back to
      //    step 1 to see which ones (the backend would reject the cancel anyway).
      if (pendingRefunds.length > 0) {
        setProgress(
          `Refunding ${pendingRefunds.length} ticket${pendingRefunds.length === 1 ? "" : "s"}…`
        );
        const failed = await runRefunds();
        if (failed > 0) {
          setProgress("");
          setFinalizing(false);
          showToast(
            `Couldn't refund ${failed} ticket${failed === 1 ? "" : "s"}. Check step 1 and finish again.`,
            { type: "error" }
          );
          setStep(0);
          return;
        }
      }

      // 2. Discount coupon (best-effort). Create it FIRST so we only promise a code
      //    that exists, but a coupon failure must NEVER block the cancel.
      let couponOk = true;
      if (discountEnabled && discountLine && !couponCreated) {
        setProgress("Setting up the discount…");
        try {
          const payload = {
            code: code.trim().toUpperCase(),
            discountType,
            maxUses: Math.max(1, Math.floor(Number(maxUses) || 1)),
            active: true,
          };
          // A super admin picking several events → one multi-event coupon (backend
          // requires the claim). Otherwise a single-event coupon the caller manages.
          if (allowAnyEvent && targetEventIDs.length > 1) payload.eventIDs = targetEventIDs;
          else payload.eventID = targetEventIDs[0];
          if (discountType === "percentage") payload.percentageOff = Number(percentageOff);
          await callFunction("adminCreateCoupon", payload);
          setCouponCreated(true);
        } catch (couponErr) {
          console.error("Discount coupon creation failed; cancelling without it:", couponErr);
          couponOk = false;
        }
      }

      // 3. Cancel + notify. Only include the discount line if the coupon is live, so
      //    attendees are never texted a code that doesn't work.
      setProgress("Cancelling the event…");
      const noteToSend = (couponOk ? finalNote : note.trim()) || null;
      await EventAPI.cancelEvent(eventID, {
        reason: internalReason.trim() || null,
        note: noteToSend,
      });

      if (!couponOk) {
        // The make-good code belongs to the event the host picked in step 3, not
        // the one being cancelled, so name it. Coupons now live in a section on
        // each event's own page, and there is no Coupons page for a host to open.
        const failedTarget =
          targetEventIDs.length === 1
            ? otherEvents.find((e) => e.id === targetEventIDs[0])?.name
            : null;
        showToast(
          `Event cancelled and attendees notified. The discount code couldn't be created, so it wasn't included. You can add one from the Coupons section on ${failedTarget || "the event you picked for the discount"}.`,
          { type: "warning" }
        );
      } else {
        showToast("Event cancelled. Attendees notified.", { type: "success" });
      }
      onCancelled?.();
      onClose();
    } catch (err) {
      // Refunds already succeeded by this point; only the coupon/cancel can be here.
      // The cancel most often fails because a ticket was bought AFTER the wizard
      // opened (the event stays live until it's cancelled, and the backend re-checks
      // refunds against fresh data). Re-fetch so a retry sees and refunds the new
      // ticket instead of looping against the stale mount-time snapshot.
      console.error("Cancellation wizard finalize failed:", err);
      const fresh = await loadPayments();
      const newlyPending = fresh
        ? (fresh.payments || []).filter(
            (p) => refundStateOf(fresh.refunds[p.paymentIntentId]) === null
          )
        : [];
      if (newlyPending.length > 0) {
        showToast(
          "Someone bought a ticket while you were here. It's shown in step 1 — finish again to refund it and cancel.",
          { type: "error" }
        );
        setStep(0);
      } else {
        showToast(
          err?.message || "Could not cancel the event. Any refunds went through. Try finishing again.",
          { type: "error" }
        );
      }
    } finally {
      setFinalizing(false);
      setProgress("");
    }
  };

  // While finishing, lock the modal so a stray backdrop click can't dismiss it
  // mid-request (refunds/coupon/cancel are all in flight).
  const busy = finalizing;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Cancel “{eventName}”
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="shrink-0 border-b border-gray-100 px-5 py-3 dark:border-gray-700/60">
          <StepBar step={step} />
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {step === 0 && (
            <RefundStep
              loading={loadingPayments}
              loadError={loadError}
              onRetryLoad={loadPayments}
              payments={payments}
              eventUsers={eventData?.eventUsers}
              paymentState={paymentState}
              rowError={rowError}
              pendingCount={pendingRefunds.length}
              pendingTotal={pendingRefundTotal}
            />
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className={LABEL}>Message to attendees</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={5}
                  maxLength={600}
                  className={INPUT}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Posted in the event chat and sent by text and push to everyone who RSVP'd. {note.length}/600
                </p>
              </div>
              <div>
                <label className={LABEL}>Internal reason (optional, not shown to attendees)</label>
                <input
                  type="text"
                  value={internalReason}
                  onChange={(e) => setInternalReason(e.target.value)}
                  placeholder="e.g. venue fell through"
                  className={INPUT}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <DiscountStep
              enabled={discountEnabled}
              onToggle={enableDiscount}
              loadingEvents={loadingEvents}
              otherEvents={otherEvents}
              multi={allowAnyEvent}
              targetEventIDs={targetEventIDs}
              setTargetEventIDs={setTargetEventIDs}
              toggleTarget={toggleTarget}
              eventSearch={eventSearch}
              setEventSearch={setEventSearch}
              discountType={discountType}
              setDiscountType={setDiscountType}
              percentageOff={percentageOff}
              setPercentageOff={setPercentageOff}
              code={code}
              setCode={setCode}
              maxUses={maxUses}
              setMaxUses={setMaxUses}
              discountMessage={discountMessage}
              onDiscountMessageChange={onDiscountMessageChange}
              onResetMessage={resetDiscountMessage}
              messageEdited={messageEdited}
              codeInMessage={codeInMessage}
            />
          )}

          {step === 3 && (
            <ReviewStep
              paymentsCount={payments.length}
              pendingCount={pendingRefunds.length}
              pendingTotal={pendingRefundTotal}
              finalNote={finalNote}
              note={note}
              setNote={setNote}
              discountEnabled={discountEnabled}
              discountType={discountType}
              percentageOff={percentageOff}
              code={code}
              maxUses={maxUses}
              targetCount={targetEventIDs.length}
              otherEvents={otherEvents}
              targetEventIDs={targetEventIDs}
              discountMessage={discountMessage}
              onDiscountMessageChange={onDiscountMessageChange}
              onResetMessage={resetDiscountMessage}
              messageEdited={messageEdited}
              codeInMessage={codeInMessage}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Never mind
            </button>
          )}

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed || busy}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primaryDark disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={finalize}
              disabled={finalizing || note.trim().length === 0 || !discountValid}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {finalizing && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
              {finalizing ? progress || "Working…" : "Cancel this event"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RefundStep({
  loading,
  loadError,
  onRetryLoad,
  payments,
  eventUsers,
  paymentState,
  rowError,
  pendingCount,
  pendingTotal,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
          Couldn't load this event's tickets
        </p>
        <p className="max-w-xs text-xs text-gray-500 dark:text-gray-400">
          We can't confirm whether anyone paid, so cancelling is paused until this loads.
        </p>
        <button
          type="button"
          onClick={onRetryLoad}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <CheckCircleIcon className="h-8 w-8 text-green-500" />
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">No paid tickets</p>
        <p className="max-w-xs text-xs text-gray-500 dark:text-gray-400">
          Nobody paid for this event, so there's nothing to refund. You can move on to the note.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {pendingCount === 0
          ? "Everyone who paid has already been refunded."
          : `${pendingCount} ticket${pendingCount === 1 ? "" : "s"} ($${pendingTotal.toFixed(
              2
            )}) will be fully refunded when you finish.`}
      </p>

      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {payments.map((p) => {
          const user = eventUsers?.[p.userID];
          const state = paymentState(p.paymentIntentId);
          const err = rowError[p.paymentIntentId];
          return (
            <li key={p.paymentIntentId} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                {user?.photoURL ? (
                  <img src={variantURL(user.photoURL, 200)} onError={fallbackToOriginal(user.photoURL)} alt="" className="h-7 w-7 flex-shrink-0 rounded-full object-cover" />
                ) : (
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {user?.name?.[0] ?? "?"}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm text-gray-800 dark:text-gray-100">
                    {user?.name ?? p.userID ?? "Unknown"}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    ${Number(p.amountDollars || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {err ? (
                  <span className="text-xs text-red-600 dark:text-red-400">{err}</span>
                ) : state === "refunded" || state === "processing" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                    <CheckIcon className="h-3 w-3" />
                    {state === "refunded" ? "Refunded" : "Refunding"}
                  </span>
                ) : (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                    Will refund
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DiscountStep({
  enabled,
  onToggle,
  loadingEvents,
  otherEvents,
  multi,
  targetEventIDs,
  setTargetEventIDs,
  toggleTarget,
  eventSearch,
  setEventSearch,
  discountType,
  setDiscountType,
  percentageOff,
  setPercentageOff,
  code,
  setCode,
  maxUses,
  setMaxUses,
  discountMessage,
  onDiscountMessageChange,
  onResetMessage,
  messageEdited,
  codeInMessage,
}) {
  const filteredEvents =
    multi && eventSearch.trim()
      ? otherEvents.filter((e) =>
          (e.name || "").toLowerCase().includes(eventSearch.trim().toLowerCase())
        )
      : otherEvents;
  return (
    <div className="space-y-4">
      <label className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">
            Offer a discount on another event
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            Optional. Gives the people you're cancelling on a reason to come back.
          </span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => onToggle(!enabled)}
          className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
            enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              enabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </label>

      {enabled && (
        <div className="space-y-4 border-t border-gray-100 pt-4 dark:border-gray-700">
          {loadingEvents ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          ) : otherEvents.length === 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              {multi
                ? "There are no other upcoming events to discount. Turn this off to continue."
                : "You don't have any other upcoming events to discount. Turn this off to continue."}
            </p>
          ) : (
            <>
              <div>
                <label className={LABEL}>
                  {multi ? "Which events" : "Which event"}
                  {multi && targetEventIDs.length > 0 && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                      ({targetEventIDs.length} selected)
                    </span>
                  )}
                </label>
                {multi ? (
                  <>
                    <input
                      type="text"
                      value={eventSearch}
                      onChange={(e) => setEventSearch(e.target.value)}
                      placeholder="Search events…"
                      className={`${INPUT} mb-2`}
                    />
                    <div className="max-h-40 overflow-y-auto rounded-md border border-gray-300 dark:border-gray-600">
                      {filteredEvents.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                          No events found.
                        </div>
                      ) : (
                        filteredEvents.map((e) => (
                          <label
                            key={e.id}
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50"
                          >
                            <input
                              type="checkbox"
                              checked={targetEventIDs.includes(e.id)}
                              onChange={() => toggleTarget(e.id)}
                              className="h-4 w-4 rounded border-gray-300 text-primary dark:border-gray-600"
                            />
                            <span className="truncate">
                              {e.name}
                              {typeof e.cost === "number" && e.cost > 0 ? ` ($${e.cost})` : ""}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <select
                    value={targetEventIDs[0] || ""}
                    onChange={(e) => setTargetEventIDs(e.target.value ? [e.target.value] : [])}
                    className={INPUT}
                  >
                    <option value="">Pick an event</option>
                    {otherEvents.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                        {typeof e.cost === "number" && e.cost > 0 ? ` ($${e.cost})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className={LABEL}>Discount</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      checked={discountType === "percentage"}
                      onChange={() => setDiscountType("percentage")}
                    />
                    Percentage off
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      checked={discountType === "free"}
                      onChange={() => setDiscountType("free")}
                    />
                    Free (100% off)
                  </label>
                </div>
              </div>

              {discountType === "percentage" && (
                <div>
                  <label className={LABEL}>Percentage off</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={percentageOff}
                    onChange={(e) => setPercentageOff(e.target.value)}
                    className={INPUT}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onBlur={(e) => setCode(e.target.value.trim().toUpperCase())}
                    placeholder="SORRY20"
                    className={`${INPUT} font-mono uppercase`}
                  />
                </div>
                <div>
                  <label className={LABEL}>Max uses</label>
                  <input
                    type="number"
                    min={1}
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    className={INPUT}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className={LABEL}>Discount message</label>
                  {messageEdited && (
                    <button
                      type="button"
                      onClick={onResetMessage}
                      className="mb-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <textarea
                  value={discountMessage}
                  onChange={(e) => onDiscountMessageChange(e.target.value)}
                  rows={3}
                  maxLength={400}
                  placeholder="Tell attendees about the discount and include the code."
                  className={INPUT}
                />
                {!codeInMessage ? (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Include the code {code.trim().toUpperCase()} so people can redeem it.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Added to the end of your cancellation message.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReviewStep({
  paymentsCount,
  pendingCount,
  pendingTotal,
  finalNote,
  note,
  setNote,
  discountEnabled,
  discountType,
  percentageOff,
  code,
  maxUses,
  targetCount,
  otherEvents,
  targetEventIDs,
  discountMessage,
  onDiscountMessageChange,
  onResetMessage,
  messageEdited,
  codeInMessage,
}) {
  const hasDiscount = discountEnabled && targetCount > 0;
  const discountSummary = (() => {
    if (!hasDiscount) return "None";
    const amount = discountType === "free" ? "Free" : `${Number(percentageOff)}% off`;
    const where =
      targetCount === 1
        ? otherEvents.find((e) => e.id === targetEventIDs[0])?.name || "1 event"
        : `${targetCount} events`;
    return `${amount} on ${where} · code ${code.trim().toUpperCase()} · up to ${maxUses} uses`;
  })();

  const refundSummary =
    paymentsCount === 0
      ? "No paid tickets"
      : pendingCount === 0
      ? "Already refunded"
      : `${pendingCount} ticket${pendingCount === 1 ? "" : "s"} · $${pendingTotal.toFixed(2)}`;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/30">
        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Finishing refunds any paid tickets, {hasDiscount ? "sets up the discount, " : ""}
          drops the event out of Discover, and texts everyone who RSVP'd your message. You can
          restore it later, but attendees won't be re-notified.
        </p>
      </div>

      <dl className="space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500 dark:text-gray-400">Refunds</dt>
          <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{refundSummary}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500 dark:text-gray-400">Discount</dt>
          <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{discountSummary}</dd>
        </div>
      </dl>

      {/* Final edits — same fields as the earlier steps, editable here too. */}
      <div>
        <label className={LABEL}>Message to attendees</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={600}
          className={INPUT}
        />
      </div>

      {hasDiscount && (
        <div>
          <div className="flex items-center justify-between">
            <label className={LABEL}>Discount message</label>
            {messageEdited && (
              <button
                type="button"
                onClick={onResetMessage}
                className="mb-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Reset to default
              </button>
            )}
          </div>
          <textarea
            value={discountMessage}
            onChange={(e) => onDiscountMessageChange(e.target.value)}
            rows={3}
            maxLength={400}
            className={INPUT}
          />
          {!codeInMessage && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Include the code {code.trim().toUpperCase()} so people can redeem it.
            </p>
          )}
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          What attendees will get
        </p>
        <div className="whitespace-pre-wrap rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
          {finalNote || "(no message)"}
        </div>
      </div>
    </div>
  );
}
